import { pluralCategory } from '@/i18n/format.ts'
import type { Messages } from '@/i18n/messages/en/index.ts'

/**
 * `feedback` in Brazilian Portuguese. Declared, never inferred, so a gap fails `tsc`.
 *
 * ── As duas promessas ──
 *
 * `noteHint` e `shareHint` são frases de peso jurídico: descrevem exatamente quem vê o quê.
 * Cada oração do inglês está aqui, sem nenhuma a mais. `noteHint` diz as DUAS metades — quem
 * é responsável pelo agente não vê o seu nome, e administradores veem. `shareHint` diz o que
 * sai (só a pergunta) e as três coisas que ficam: a resposta, o resto da conversa e o seu
 * nome.
 *
 * ── Gênero ──
 *
 * "owner" não tem forma neutra em português — *o dono* presume um homem. Onde o inglês diz
 * "the owner" / "they", aqui se diz "a pessoa responsável" (pessoa é feminino, e vale para
 * qualquer pessoa) ou "quem recebe". O mesmo vale para "the person who owns this agent" em
 * `recorded` (PRD-27 D-21).
 *
 * "note" é `nota` em toda parte — o mesmo termo que `approvals` usa para a nota da decisão,
 * porque uma palavra por ideia vale também para as que não estão no glossário.
 */
export const feedback: Messages['feedback'] = {
  memoryInfluenced: 'Moldada pelas suas preferências salvas',
  memoryInfluencedLabel: 'Moldada pelas suas preferências salvas — veja as preferências que este agente usa',

  helpful: 'Útil',
  unhelpful: 'Não útil',
  helpfulLabel: 'Marcar esta resposta como útil',
  unhelpfulLabel: 'Marcar esta resposta como não útil',
  recorded: 'Obrigado — isto chega até a pessoa responsável por este agente.',
  voteFailed: 'Não foi possível salvar. Tente de novo.',

  addNote: 'Adicionar uma nota',
  noteLabel: 'O que deu errado, ou o que funcionou — opcional',
  notePlaceholder: 'Faltou a parte sobre o último trimestre.',
  noteHint: 'A pessoa responsável por este agente vê isto sem o seu nome. Administradores podem ver quem escreveu.',
  /** Zero fica no singular em português: "0 caractere restante". */
  noteRemaining: (fmt, left) =>
    pluralCategory(fmt.locale, left) === 'one' ? `${left} caractere restante` : `${left} caracteres restantes`,
  noteSend: 'Enviar nota',
  noteSending: 'Enviando…',
  noteSent: 'Nota enviada.',
  noteFailed: 'Não foi possível enviar a nota. Nada foi perdido — tente de novo.',
  noteCancel: 'Cancelar',

  shareOffer: 'Compartilhar esta pergunta com a pessoa responsável pelo agente',
  shareTitle: 'Envie sua pergunta para a pessoa responsável',
  shareHint:
    'Quem recebe vê a pergunta que você enviar aqui — não vê a resposta, nem o resto desta conversa, nem o seu nome. Edite antes de enviar.',
  shareLabel: 'A pergunta que será enviada',
  shareSend: 'Enviar pergunta',
  shareSending: 'Enviando…',
  shareSent: 'Enviada. A pessoa responsável pode usar isso para melhorar este agente.',
  shareFailed: 'Não foi possível enviar. Nada foi perdido — tente de novo.',
  shareCancel: 'Agora não'
}
